import { PACK_IDS } from '../shared/game';
import { HudFrame } from './HudFrame';

type Vec3Like = {
  x?: number;
  y?: number;
  z?: number;
};

export type HudBaseEntitySnapshot = {
  id?: string;
  team?: string | null;
  position?: Vec3Like;
  radius?: number | null;
  active?: boolean | null;
  powered?: boolean | null;
  networkState?: string | null;
  health?: number | null;
  respawnSeconds?: number | null;
};

type HudLocalPlayerSnapshot = {
  team?: string | null;
  armorClass?: string | null;
  position?: Vec3Like;
  carryingFlagTeam?: string | null;
  packSlot?: number | null;
  packId?: string | null;
  packLabel?: string | null;
  packType?: string | null;
  packState?: string | null;
  packActive?: boolean;
  packReady?: boolean;
  packCooldownSeconds?: number;
};

type BaseSystemsPanelProps = {
  localPlayer: HudLocalPlayerSnapshot;
  generators: HudBaseEntitySnapshot[];
  sensors: HudBaseEntitySnapshot[];
  shieldDoors: HudBaseEntitySnapshot[];
  inventoryStations: HudBaseEntitySnapshot[];
  repairZones: HudBaseEntitySnapshot[];
  turrets: HudBaseEntitySnapshot[];
  compact?: boolean;
  minimal?: boolean;
};

function teamTone(team?: string | null) {
  return team === 'red' ? 'red' : team === 'blue' ? 'cyan' : 'neutral';
}

function safeVec3(value?: Vec3Like | null) {
  return {
    x: value?.x ?? 0,
    y: value?.y ?? 0,
    z: value?.z ?? 0,
  };
}

function distance(a: Vec3Like | null | undefined, b: Vec3Like | null | undefined) {
  const left = safeVec3(a);
  const right = safeVec3(b);
  return Math.hypot(left.x - right.x, left.y - right.y, left.z - right.z);
}

function formatDistance(value: number) {
  if (value < 10) {
    return `${value.toFixed(1)}m`;
  }

  return `${Math.round(value)}m`;
}

function toLabel(value?: string | null) {
  return value ? value.replace(/[_-]/g, ' ').toUpperCase() : 'UNKNOWN';
}

function serviceCount(entries: HudBaseEntitySnapshot[], team: string | null | undefined, key: 'active' | 'powered') {
  return entries.filter((entry) => entry.team === team && Boolean(entry[key])).length;
}

function teamCount(entries: HudBaseEntitySnapshot[], team: string | null | undefined) {
  return entries.filter((entry) => entry.team === team).length;
}

function onlineCount(
  entries: HudBaseEntitySnapshot[],
  team: string | null | undefined,
  predicate: (entry: HudBaseEntitySnapshot) => boolean,
) {
  return entries.filter((entry) => entry.team === team && predicate(entry)).length;
}

function generatorStatus(generator?: HudBaseEntitySnapshot) {
  if (!generator || generator.active === false || generator.powered === false) {
    return 'OFFLINE';
  }

  if (generator.active) {
    return typeof generator.health === 'number' && Number.isFinite(generator.health)
      ? `ONLINE ${Math.round(generator.health)} HP`
      : 'ONLINE';
  }

  const respawn = Math.max(0, Math.ceil(generator.respawnSeconds ?? 0));
  return respawn > 0 ? `DOWN ${respawn}s` : 'OFFLINE';
}

function sensorSummary(sensors: HudBaseEntitySnapshot[], team: string | null | undefined) {
  const total = teamCount(sensors, team);
  const online = onlineCount(sensors, team, (sensor) => sensor.active !== false && sensor.powered !== false);

  return {
    active: online > 0,
    value: `${online}/${total} ${online > 0 ? 'ONLINE' : 'OFFLINE'}`,
  };
}

function shieldDoorSummary(doors: HudBaseEntitySnapshot[], team: string | null | undefined) {
  const ownedDoors = doors.filter((door) => door.team === team);
  const onlineDoors = ownedDoors.filter((door) => (door.powered ?? door.active) !== false).length;

  return {
    total: ownedDoors.length,
    online: onlineDoors,
    active: onlineDoors > 0,
    value: ownedDoors.length ? `${onlineDoors}/${ownedDoors.length} ${onlineDoors > 0 ? 'ONLINE' : 'OFFLINE'}` : 'UNTRACKED',
  };
}

function turretNetworkSummary(turrets: HudBaseEntitySnapshot[], team: string | null | undefined) {
  const ownedTurrets = turrets.filter((turret) => turret.team === team);
  const networkState = ownedTurrets.find((turret) => typeof turret.networkState === 'string')?.networkState ?? null;
  const activeTurrets = ownedTurrets.filter((turret) => turret.active).length;

  if (networkState === 'power-offline') {
    return { active: false, badge: 'OFFLINE', value: 'POWER OFFLINE', tone: '#ff8d74' };
  }

  if (networkState === 'sensor-offline') {
    return { active: false, badge: 'DEGRADED', value: 'SENSOR OFFLINE', tone: '#ffcc66' };
  }

  return {
    active: activeTurrets > 0,
    badge: activeTurrets > 0 ? 'ONLINE' : 'DOWN',
    value: activeTurrets > 0 ? 'ONLINE' : 'DOWN',
    tone: activeTurrets > 0 ? '#62ddff' : '#9db6c8',
  };
}

function summaryStripStyle(active: boolean, tone: string) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '5px 8px',
    border: `1px solid ${active ? `${tone}33` : 'rgba(255,255,255,0.08)'}`,
    background: active ? `${tone}12` : 'rgba(255,255,255,0.03)',
    clipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))',
    WebkitClipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))',
    fontSize: 10,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    color: active ? '#f4fbff' : 'rgba(231,244,252,0.68)',
    minWidth: 0,
  } as const;
}

type GeneratorPressureState = 'stable' | 'warning' | 'critical' | 'offline';

function resolveGeneratorPressure(generator?: HudBaseEntitySnapshot) {
  const health = typeof generator?.health === 'number' && Number.isFinite(generator.health) ? generator.health : null;

  if (!generator || generator.active === false || generator.powered === false) {
    const respawn = Math.max(0, Math.ceil(generator?.respawnSeconds ?? 0));

    return {
      state: 'offline' as GeneratorPressureState,
      label: respawn > 0 ? `OFFLINE ${respawn}s` : 'OFFLINE',
      tone: '#9db6c8',
    };
  }

  if (health != null && health <= 25) {
    return {
      state: 'critical' as GeneratorPressureState,
      label: 'CRITICAL',
      tone: '#ff8d74',
    };
  }

  if (health != null && health <= 60) {
    return {
      state: 'warning' as GeneratorPressureState,
      label: 'WARNING',
      tone: '#ffcc66',
    };
  }

  return {
    state: 'stable' as GeneratorPressureState,
    label: 'STABLE',
    tone: '#62ddff',
  };
}

function formatPackSummary(localPlayer: HudLocalPlayerSnapshot) {
  const slot = localPlayer.packSlot ?? null;
  const label = localPlayer.packLabel ?? localPlayer.packType ?? localPlayer.packId ?? (slot ? `Pack ${slot}` : 'Pack');
  const cooldownSeconds = Math.max(0, localPlayer.packCooldownSeconds ?? 0);
  const active = Boolean(localPlayer.packActive);
  const ready = Boolean(localPlayer.packReady ?? (!active && cooldownSeconds <= 0));
  const state =
    localPlayer.packState && localPlayer.packState.length ? localPlayer.packState.toUpperCase() : active ? 'ACTIVE' : ready ? 'READY' : `COOLDOWN ${cooldownSeconds.toFixed(1)}S`;

  return {
    label: label.replace(/[_-]+/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase()),
    state,
  };
}

function formatPackSlotKeys() {
  return PACK_IDS.map((_, index) => String(index + 5)).join('-');
}

function StatusChip({
  label,
  value,
  active,
  badge,
  tone = '#62ddff',
}: {
  label: string;
  value: string;
  active: boolean;
  badge?: string;
  tone?: string;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gap: 5,
        padding: '9px 10px',
        background: active ? `${tone}14` : 'rgba(255,255,255,0.03)',
        border: `1px solid ${active ? `${tone}33` : 'rgba(255,255,255,0.08)'}`,
        boxShadow: active ? `0 0 12px ${tone}08` : 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, minWidth: 0 }}>
        <span style={{ fontSize: 10, letterSpacing: 1.6, textTransform: 'uppercase', color: 'rgba(222, 240, 250, 0.58)' }}>{label}</span>
        {badge ? (
          <span
            style={{
              flexShrink: 0,
              padding: '2px 6px',
              border: `1px solid ${active ? `${tone}40` : 'rgba(255,255,255,0.08)'}`,
              background: active ? `${tone}18` : 'rgba(255,255,255,0.03)',
              fontSize: 9,
              letterSpacing: 1.2,
              textTransform: 'uppercase',
              color: active ? tone : 'rgba(231,244,252,0.62)',
            }}
          >
            {badge}
          </span>
        ) : null}
      </div>
      <strong style={{ fontSize: 12.5, letterSpacing: 1, textTransform: 'uppercase', color: active ? '#f4fbff' : 'rgba(226,239,247,0.72)' }}>{value}</strong>
    </div>
  );
}

export function BaseSystemsPanel({
  localPlayer,
  generators,
  sensors,
  shieldDoors,
  inventoryStations,
  repairZones,
  turrets,
  compact = false,
  minimal = false,
}: BaseSystemsPanelProps) {
  const localTeam = localPlayer.team ?? null;
  const localGenerator = generators.find((entry) => entry.team === localTeam);
  const enemyGenerator = generators.find((entry) => entry.team && entry.team !== localTeam) ?? generators[0];
  const friendlyInventoryStations = inventoryStations.filter((entry) => entry.team === localTeam);
  const nearestFriendlyInventory = friendlyInventoryStations.reduce<HudBaseEntitySnapshot | null>((closest, entry) => {
    if (!closest) {
      return entry;
    }

    return distance(entry.position, localPlayer.position) < distance(closest.position, localPlayer.position) ? entry : closest;
  }, null);
  const nearbyPoweredInventory = friendlyInventoryStations.find((entry) => entry.powered && distance(entry.position, localPlayer.position) <= (entry.radius ?? 0) + 2) ?? null;
  const nearbyOfflineInventory = friendlyInventoryStations.find((entry) => !entry.powered && distance(entry.position, localPlayer.position) <= (entry.radius ?? 0) + 2) ?? null;
  const carryingFlagTeam = localPlayer.carryingFlagTeam ?? null;
  const nearestDistance = nearestFriendlyInventory?.position ? distance(nearestFriendlyInventory.position, localPlayer.position) : null;
  const localGeneratorPressure = resolveGeneratorPressure(localGenerator);
  const enemyGeneratorPressure = resolveGeneratorPressure(enemyGenerator);
  const sensor = sensorSummary(sensors, localTeam);
  const shield = shieldDoorSummary(shieldDoors, localTeam);
  const turret = turretNetworkSummary(turrets, localTeam);

  const inventoryOnline = serviceCount(inventoryStations, localTeam, 'powered');
  const repairOnline = serviceCount(repairZones, localTeam, 'powered');
  const pack = formatPackSummary(localPlayer);
  const packSlotKeys = formatPackSlotKeys();
  const loadoutState = carryingFlagTeam
    ? {
        label: 'LOADOUT LOCKED',
        detail: 'Flag carrier loadouts are locked. Return the flag to unlock armor and packs.',
        tone: '#ffcc66',
      }
    : localGeneratorPressure.state === 'critical'
      ? {
          label: 'POWER CRITICAL',
          detail: nearbyPoweredInventory
            ? 'Inventory station online, but generator pressure is critical.'
            : 'Generator pressure is critical. Reach a powered station or restore base power.',
          tone: localGeneratorPressure.tone,
        }
      : localGeneratorPressure.state === 'warning'
        ? {
            label: 'POWER WARNING',
            detail: nearbyPoweredInventory
              ? 'Inventory station online, but generator pressure is strained.'
              : 'Generator pressure is warning-level. Power is under strain.',
            tone: localGeneratorPressure.tone,
          }
        : localGeneratorPressure.state === 'offline'
          ? {
              label: 'POWER OFFLINE',
              detail: nearbyPoweredInventory
                ? 'Inventory station is in range, but base power is offline.'
                : 'Generator offline. Move to a powered inventory station or restore base power.',
              tone: localGeneratorPressure.tone,
            }
    : nearbyPoweredInventory
      ? {
          label: 'POWER STABLE',
          detail: `Friendly inventory station in range. F1-F3 swap armor; ${packSlotKeys} selects packs.`,
          tone: localGeneratorPressure.tone,
        }
      : nearbyOfflineInventory
        ? {
            label: 'STATION UNPOWERED',
            detail: 'Friendly inventory station is in range, but power is offline.',
            tone: '#ffcc66',
          }
        : localTeam
          ? {
              label: 'MOVE TO STATION',
              detail:
                nearestDistance != null
                  ? `Nearest friendly inventory pad is ${formatDistance(nearestDistance)} away.`
                  : 'Move to a powered friendly inventory station to change armor and packs.',
              tone: '#edf7ff',
          }
          : {
              label: 'AWAIT TEAM',
              detail: 'Join a team before loadout changes become available.',
              tone: 'rgba(231,244,252,0.72)',
            };
  const prompt = nearbyPoweredInventory
    ? `F1 LIGHT  F2 MEDIUM  F3 HEAVY  |  PACKS ${packSlotKeys} ${pack.label.toUpperCase()} ${pack.state}`
    : `${loadoutState.detail} ${localTeam ? `(${pack.state})` : ''}`.trim();

  if (minimal) {
    const servicesTone =
      localGeneratorPressure.state === 'offline'
        ? '#9db6c8'
        : localGeneratorPressure.state === 'critical'
          ? '#ff8d74'
          : localGeneratorPressure.state === 'warning'
            ? '#ffcc66'
            : '#62ddff';

    return (
      <HudFrame
        title="Base"
        subtitle={localGeneratorPressure.state === 'stable' ? 'Power stable' : `Generator ${localGeneratorPressure.label.toLowerCase()}`}
        tone={teamTone(localTeam)}
        trailing={
          <div
            style={{
              padding: '5px 7px',
              border: '1px solid rgba(255,255,255,0.10)',
              background: 'rgba(255,255,255,0.04)',
              fontSize: 10,
              letterSpacing: 1.4,
              textTransform: 'uppercase',
              color: '#f4fbff',
            }}
          >
            {toLabel(localPlayer.armorClass)}
          </div>
        }
        style={{ minWidth: 0, backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
      >
        <div style={{ display: 'grid', gap: 7 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            <span style={summaryStripStyle(localGeneratorPressure.state !== 'offline', servicesTone)}>
              {`${toLabel(localGenerator?.team)} ${localGeneratorPressure.label}`}
            </span>
            <span style={summaryStripStyle(enemyGeneratorPressure.state !== 'offline', enemyGeneratorPressure.tone)}>
              {`${toLabel(enemyGenerator?.team)} ${enemyGeneratorPressure.label}`}
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            <span style={summaryStripStyle(sensor.active, sensor.active ? '#62ddff' : '#ffcc66')}>{`S ${sensor.value}`}</span>
            <span style={summaryStripStyle(shield.active, shield.active ? '#8ef0c9' : '#ff8d74')}>{`D ${shield.value}`}</span>
            <span style={summaryStripStyle(turret.active, turret.tone)}>{`T ${turret.value}`}</span>
          </div>
          {loadoutState.label !== 'MOVE TO STATION' ? (
            <span
              style={{
                padding: '6px 8px',
                border: `1px solid ${loadoutState.tone}30`,
                background: `${loadoutState.tone}10`,
                clipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))',
                WebkitClipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))',
                color: '#edf7ff',
                fontSize: 10.5,
                lineHeight: 1.28,
              }}
            >
              {loadoutState.label}
            </span>
          ) : null}
        </div>
      </HudFrame>
    );
  }

  return (
    <HudFrame
      title="Base Status"
      subtitle={
        localGeneratorPressure.state === 'stable'
          ? 'Generator power, sensor net, shield doors, and loadout control'
          : `Generator ${localGeneratorPressure.label.toLowerCase()}; base services may be limited`
      }
      tone={teamTone(localTeam)}
      trailing={
        <div
          style={{
            padding: compact ? '6px 8px' : '7px 9px',
            border: '1px solid rgba(255,255,255,0.10)',
            background: 'rgba(255,255,255,0.04)',
            fontSize: 10.5,
            letterSpacing: 1.5,
            textTransform: 'uppercase',
            color: '#f4fbff',
          }}
        >
          {toLabel(localPlayer.armorClass)}
        </div>
      }
      style={{ minWidth: 0, backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
    >
      <div style={{ display: 'grid', gap: compact ? 8 : 10 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
          <StatusChip
            label={`${toLabel(localGenerator?.team)} gen`}
            value={generatorStatus(localGenerator)}
            active={localGeneratorPressure.state !== 'offline'}
            badge={localGeneratorPressure.label}
            tone={localGeneratorPressure.tone}
          />
          <StatusChip
            label={`${toLabel(enemyGenerator?.team)} gen`}
            value={generatorStatus(enemyGenerator)}
            active={enemyGeneratorPressure.state !== 'offline'}
            badge={enemyGeneratorPressure.label}
            tone={enemyGeneratorPressure.tone}
          />
        </div>

        {!minimal ? (
          <div style={{ display: 'grid', gridTemplateColumns: compact ? 'repeat(2, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
            <StatusChip label="Inventory" value={`${inventoryOnline} online`} active={inventoryOnline > 0} />
            <StatusChip label="Repair" value={`${repairOnline} online`} active={repairOnline > 0} />
          </div>
        ) : null}

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <span style={summaryStripStyle(sensor.active, sensor.active ? '#62ddff' : '#ffcc66')}>{`SENSOR ${sensor.value}`}</span>
          <span style={summaryStripStyle(shield.active, shield.active ? '#8ef0c9' : '#ff8d74')}>{`SHIELD ${shield.value}`}</span>
          <span style={summaryStripStyle(turret.active, turret.tone)}>{`TURRET NET ${turret.value}`}</span>
        </div>

        <div
          style={{
            display: 'grid',
            gap: 4,
            padding: compact ? '8px 10px' : '9px 11px',
            border: `1px solid ${
              loadoutState.label.startsWith('POWER ')
                ? `${loadoutState.tone}40`
                : loadoutState.label === 'LOADOUT LOCKED'
                  ? 'rgba(255,204,102,0.24)'
                  : loadoutState.label === 'STATION UNPOWERED'
                    ? 'rgba(255,204,102,0.18)'
                    : 'rgba(255,255,255,0.08)'
            }`,
            background:
              loadoutState.label.startsWith('POWER ')
                ? `${loadoutState.tone}14`
                : loadoutState.label === 'LOADOUT LOCKED'
                  ? 'rgba(255,204,102,0.08)'
                  : loadoutState.label === 'STATION UNPOWERED'
                    ? 'rgba(255,204,102,0.05)'
                    : 'rgba(255,255,255,0.03)',
          }}
        >
          <span
            style={{
              fontSize: 10,
              letterSpacing: 1.7,
              textTransform: 'uppercase',
              color: loadoutState.tone,
            }}
          >
            {loadoutState.label}
          </span>
          <strong style={{ fontSize: compact ? 11.5 : 12.5, lineHeight: 1.35, color: '#edf7ff' }}>{prompt}</strong>
        </div>

        {minimal ? null : (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
            }}
          >
            <span
              style={{
                padding: '5px 9px',
                border: '1px solid rgba(98,221,255,0.18)',
                background: 'rgba(98,221,255,0.08)',
                clipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))',
                WebkitClipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))',
                fontSize: 10,
                letterSpacing: 1.2,
                textTransform: 'uppercase',
                color: '#f4fbff',
              }}
            >
              {pack.label}
            </span>
            <span
              style={{
                padding: '5px 9px',
                border: '1px solid rgba(255,255,255,0.08)',
                background: 'rgba(255,255,255,0.03)',
                clipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))',
                WebkitClipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))',
                fontSize: 10,
                letterSpacing: 1.2,
                textTransform: 'uppercase',
                color: 'rgba(231,244,252,0.72)',
              }}
            >
              {pack.state}
            </span>
          </div>
        )}
      </div>
    </HudFrame>
  );
}
